import React from 'react';

import { Button } from '@/design-system/button';
import { Popover, PopoverTrigger } from '@/design-system/popover';
import { FilterPopoverContent } from '@/components/FilterPopover';
import { SearchSelect } from '@/design-system/search-select';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import {
  WORKFLOW_STOP_TYPE_LABELS,
  WorkflowStopType,
} from '@/services/api/stopWorkflows';

export interface WorkflowFilterValue {
  /** Numeric provider id stringified (matches the dropdown select API). */
  clientId: string;
  /** Empty = both stop types. */
  stopType: WorkflowStopType | '';
}

export const EMPTY_FILTERS: WorkflowFilterValue = {
  clientId: '',
  stopType: '',
};

/** Single source of truth for the "Filter (N)" badge counter. */
export const countActiveFilters = (v: WorkflowFilterValue): number =>
  (v.clientId ? 1 : 0) + (v.stopType ? 1 : 0);

const STOP_TYPES: WorkflowStopType[] = ['PICKUP', 'DROP_OFF'];

interface WorkflowFilterPanelProps {
  open: boolean;
  /** Controls the Radix popover. Opening seeds the draft in the parent. */
  onOpenChange: (open: boolean) => void;
  /** Number of applied filters — shown as an ink pill on the trigger. */
  activeFilterCount: number;
  value: WorkflowFilterValue;
  onChange: (next: WorkflowFilterValue) => void;
  onApply: () => void;
  onReset: () => void;
}

/**
 * Filter popover for the Workflows list — mirrors `ShipmentFilterPanel`:
 * draft lives in the parent, values apply to the URL in one atomic patch.
 * Two filters only (client, stop type), so no date presets or batch search.
 */
const WorkflowFilterPanel: React.FC<WorkflowFilterPanelProps> = ({
  open,
  onOpenChange,
  activeFilterCount,
  value,
  onChange,
  onApply,
  onReset,
}) => {
  const labelCls = 'mb-2 block text-[11px] font-medium text-text-sub-600';

  // Same hook the shipments filter + builder use: page-1 fetch on open,
  // debounced `?search=`, lazy loadMore, id→name cache for the picked label.
  const providers = useProvidersDropdown(open);

  // Clicking the active stop type clears it (mirrors the date-preset toggle).
  const toggleStopType = (stopType: WorkflowStopType) => {
    onChange({
      ...value,
      stopType: value.stopType === stopType ? '' : stopType,
    });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button tone="neutral" style="stroke" size="sm">
          <i className="ri-equalizer-2-line mr-1.5 text-[14px]" />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-1.5 rounded-md bg-bg-strong-950 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-text-white-0">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <FilterPopoverContent
        align="end"
        className="w-[320px] p-0"
        aria-label="Filter workflows"
      >
        <div className="flex items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            Filter workflows
          </p>
          <button
            type="button"
            onClick={onReset}
            className="text-[11px] font-medium text-text-sub-600 transition-colors hover:text-accent"
          >
            Reset
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-4 px-4 py-3">
          <div>
            <p className={labelCls}>Stop type</p>
            <div className="flex flex-wrap gap-1">
              {STOP_TYPES.map((stopType) => {
                const active = value.stopType === stopType;
                return (
                  <button
                    key={stopType}
                    type="button"
                    onClick={() => toggleStopType(stopType)}
                    className={`rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors ${
                      active
                        ? 'border-accent text-accent'
                        : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
                    }`}
                  >
                    {WORKFLOW_STOP_TYPE_LABELS[stopType]}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className={labelCls}>Client</p>
            <SearchSelect
              options={providers.options.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              value={value.clientId || undefined}
              selectedLabelFallback={
                value.clientId ? providers.labelFor(value.clientId) : undefined
              }
              onChange={(v) =>
                onChange({ ...value, clientId: (v as string | undefined) ?? '' })
              }
              onSearchChange={providers.setSearch}
              disableLocalFilter
              loading={providers.loading}
              hasMore={providers.hasMore}
              onLoadMore={providers.loadMore}
              placeholder="All clients"
              allowClear
              emptyMessage={providers.error ?? 'No matching client'}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={onApply}>
            Apply
          </Button>
        </div>
      </FilterPopoverContent>
    </Popover>
  );
};

export default WorkflowFilterPanel;
